
import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    signal,
    SimpleChanges,
    forwardRef,
    inject,
} from '@angular/core';
import {
    ControlValueAccessor,
    FormArray,
    FormBuilder,
    FormGroup,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import {
    MatFormFieldAppearance,
    MatFormFieldModule,
} from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TranslatePipe } from '@ngx-translate/core';
import { SelectFieldComponent } from '../select-field/select-field.component';
import { ListaLavoriService } from './lavoro-selector.service';
import { ListaModuliService } from './modulo-selector.service';

/**
 * A modulo/lavoro option as shown in the selects (id may be a numeric id or a UUID string).
 */
export interface ModuloLavoroOption {
  id: string | number;
  name: string;
}

/**
 * A single row of the modulo/lavoro/scadenza selector's value.
 */
export interface ModuloLavoroEntry {
  moduli?: ModuloLavoroOption | null;
  lavori?: ModuloLavoroOption | null;
  scadenza?: string | Date | null;
}

@UntilDestroy()
@Component({
   
  selector: 'siat-modulo-lavoro-selector',
  templateUrl: './modulo-lavoro-selector.component.html',
  styleUrls: ['./modulo-lavoro-selector.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    SelectFieldComponent,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    TranslatePipe
],
  providers: [
    ListaModuliService,
    ListaLavoriService,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ModuloLavoroSelectorComponent),
      multi: true,
    },
  ],
})
export class ModuloLavoroSelectorComponent
  implements OnInit, OnChanges, ControlValueAccessor
{
  @Input() appearance: MatFormFieldAppearance = 'outline';
  @Input() id?: string;
  @Input() name?: string;
  @Input() label: string;
  @Input() placeholder?: string;
  @Input() struttura?: { id: number; name: string } | null;
  @Input() readonly = false;
  @Output() optionReady = new EventEmitter<boolean>();

  moduli = signal<{ id: number; name: string }[]>([]);
  isLoadingModuli = signal(false);
  lavori: { id: number; name: string }[] = [];
  isLoadingLavori = false;
  moduliDisabled = true;
  lavoriDisabled = true;

  // Store lavori options per form group to maintain independence
  private lavoriByGroup = new Map<number, { id: number; name: string }[]>();
  
  // Caching to prevent duplicate API calls
  private loadedModuliForStruttura: number | null = null;
  private loadedLavoriForModuli = new Set<string>();

  protected moduliStore = inject(ListaModuliService);
  protected lavoriStore = inject(ListaLavoriService);
  private fb = inject(FormBuilder);

  formGroup!: FormGroup;

  get formArray(): FormArray {
    return this.formGroup.get('entries') as FormArray;
  }

  get formGroups(): FormGroup[] {
    return this.formArray.controls as FormGroup[];
  }

  // Get lavori options for a specific form group - memoized to prevent infinite change detection
  private lavoriCache = new Map<
    number,
    { options: { id: number; name: string }[]; hash: string }
  >();

  getLavoriForGroup(groupIndex: number): { id: number; name: string }[] {
    const currentLavori = this.lavoriByGroup.get(groupIndex) || [];
    const hash = JSON.stringify(currentLavori);

    const cached = this.lavoriCache.get(groupIndex);
    if (cached && cached.hash === hash) {
      return cached.options;
    }

    this.lavoriCache.set(groupIndex, { options: currentLavori, hash });
    return currentLavori;
  }

  // ---- CVA ----
  private onChange: (value: ModuloLavoroEntry[]) => void = () => {};
  private onTouched: () => void = () => {};

  registerOnChange(fn: (value: ModuloLavoroEntry[]) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    this.readonly = isDisabled;
    if (isDisabled) {
      this.formGroup.disable();
    } else {
      this.formGroup.enable();
      // Apply normal business logic for enabling/disabling
      if (this.moduliDisabled) {
        this.formGroups.forEach(group => {
          group.get('moduli')?.disable();
        });
      }
      if (this.lavoriDisabled) {
        this.formGroups.forEach(group => {
          group.get('lavori')?.disable();
        });
      }
    }
  }
  writeValue(value: ModuloLavoroEntry[] | null): void {
    this.formArray.clear();
    if (value && Array.isArray(value) && value.length > 0) {
      // Collect unique moduli IDs to avoid duplicate API calls
      const uniqueModuliIds = new Set<string>();
      const normalizedValues: ModuloLavoroEntry[] = [];

      value.forEach(v => {
        // Ensure proper data format - keep both moduli and lavori IDs as strings (UUIDs)
        const normalizedValue = {
          ...v,
          moduli: v.moduli ? { 
            id: v.moduli.id, // Keep as string for UUID
            name: v.moduli.name 
          } : null,
          lavori: v.lavori ? { 
            id: v.lavori.id, // Keep as string for UUID
            name: v.lavori.name 
          } : null
        };
        normalizedValues.push(normalizedValue);
        
        // Collect unique moduli IDs for API calls
        if (normalizedValue.moduli?.id && typeof normalizedValue.moduli.id === 'string' && normalizedValue.moduli.id.length > 0) {
          uniqueModuliIds.add(normalizedValue.moduli.id);
        }
      });

      // Populate moduli and lavori options from pre-filled data
      this.populateOptionsFromPrefilledData(normalizedValues);

      // Create form groups - one for each entry in the value array
      normalizedValues.forEach(normalizedValue => {
        const group = this.createEntryGroup(normalizedValue);
        this.formArray.push(group);
        
        // Initially disable lavori control until options are loaded
        if (!this.readonly) {
          const lavoriControl = group.get('lavori');
          if (lavoriControl && !normalizedValue.lavori) {
            lavoriControl.disable();
          }
        }
        
        // Don't setup listeners in readonly mode
        if (!this.readonly) {
          this.setupModuliListener(group);
        }
      });

      // Load lavori for each unique modulo (one API call per unique modulo)
      if (!this.readonly && uniqueModuliIds.size > 0) {
        uniqueModuliIds.forEach(moduloId => {
          queueMicrotask(() => {
            this.loadLavoriForSingleModulo(moduloId, normalizedValues);
          });
        });
      }
    } else {
      const group = this.createEntryGroup();
      this.formArray.push(group);
      if (!this.readonly) {
        this.setupModuliListener(group);
      }
    }
  }

  private populateOptionsFromPrefilledData(
    normalizedValues: ModuloLavoroEntry[]
  ): void {
    // Extract unique moduli and lavori from the provided data
    const moduliSet = new Set();
    const lavoriSet = new Set();

    normalizedValues.forEach(v => {
      if (v.moduli) {
        // Keep moduli ID as string (UUID)
        moduliSet.add(JSON.stringify(v.moduli));
      }
      if (v.lavori) {
        // Keep lavori ID as string (UUID)
        lavoriSet.add(JSON.stringify(v.lavori));
      }
    });

    this.moduli.set(Array.from(moduliSet).map(m => JSON.parse(m as string)));
    this.lavori = Array.from(lavoriSet).map(l => JSON.parse(l as string));

    // Enable the controls when we have preselected data
    this.moduliDisabled = false;
    this.lavoriDisabled = false;
  }

  private loadLavoriForPreselection(moduliIds: (string | number)[]): void {
    this.lavoriStore.fetchLavoriByModuli(moduliIds).subscribe({
      next: lavori => {
        // Merge with existing lavori from preselected data
        const existingLavoriIds = this.lavori.map(l => Number(l.id));
        const newLavori = lavori
          .filter(l => !existingLavoriIds.includes(Number(l.id)))
          .map(l => ({ id: Number(l.id), name: l.nome }));

        this.lavori = [...this.lavori, ...newLavori];
        
        // After loading lavori options, update all form groups to ensure their lavori selections are preserved
        this.formGroups.forEach((group, index) => {
          const currentLavoriValue = group.get('lavori')?.value;
          if (currentLavoriValue && currentLavoriValue.id) {
            const matchingOption = this.lavori.find(l => Number(l.id) === Number(currentLavoriValue.id));
            if (matchingOption && !group.get('lavori')?.disabled) {
              // Update the lavoriByGroup map for this group
              this.lavoriByGroup.set(index, this.lavori);
              group.get('lavori')?.setValue(matchingOption, { emitEvent: false });
            }
          }
        });
      },
      error: () => {
        // Keep existing lavori on error
      },
    });
  }

  constructor() {
    const initialGroup = this.createEntryGroup();
    this.formGroup = this.fb.group({
      entries: this.fb.array([initialGroup]),
    });
    // Setup listener for initial group
    this.setupModuliListener(initialGroup);
  }

  ngOnInit(): void {
    // Load moduli if struttura is already available
    if (this.struttura?.id) {
      this.loadModuliForStruttura(this.struttura.id);
    }

    // ValueChanges -> CVA
    this.formArray.valueChanges.subscribe(value => {
      this.onChange(value);
      this.onTouched();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['readonly'] && this.readonly) {
      // When readonly becomes true, disable the entire form
      this.formGroup.disable();
    }

    if (changes['struttura'] && !changes['struttura'].firstChange) {
      const newStruttura = changes['struttura'].currentValue;
      const previousStruttura = changes['struttura'].previousValue;

      // Only react if the struttura actually changed
      if (newStruttura?.id !== previousStruttura?.id) {
        if (newStruttura?.id) {
          this.loadModuliForStruttura(newStruttura.id);
        } else {
          this.clearModuli();
        }
      }
    }
  }

  private loadModuliForStruttura(idStrutturaPadre: number): void {
    // Prevent duplicate calls for the same struttura
    if (this.loadedModuliForStruttura === idStrutturaPadre) {
      return;
    }
    
    this.loadedModuliForStruttura = idStrutturaPadre;
    this.isLoadingModuli.set(true);
    this.moduliDisabled = true;
    this.lavoriDisabled = true;
    this.moduli.set([]);
    this.lavori = [];

    // Clear and disable all moduli and lavori controls
    this.formGroups.forEach(group => {
      group.get('moduli')?.setValue(null);
      group.get('moduli')?.disable();
      group.get('lavori')?.setValue(null);
      group.get('lavori')?.disable();
    });

    this.moduliStore.fetchModuliByIdStrutturaPadre(idStrutturaPadre).subscribe({
      next: moduli => {
        this.moduli.set(moduli.map(m => ({ id: m.id, name: m.nome })));
        this.moduliDisabled = false;
        this.isLoadingModuli.set(false);

        // Enable all moduli controls
        this.formGroups.forEach(group => {
          group.get('moduli')?.enable();
        });
      },
      error: () => {
        this.moduli.set([]);
        this.moduliDisabled = true;
        this.isLoadingModuli.set(false);
        this.loadedModuliForStruttura = null; // Reset on error to allow retry

        // Keep moduli controls disabled on error
        this.formGroups.forEach(group => {
          group.get('moduli')?.disable();
        });
      },
    });
  }

  private clearModuli(): void {
    this.moduli.set([]);
    this.lavori = [];
    this.moduliDisabled = true;
    this.lavoriDisabled = true;
    this.isLoadingModuli.set(false);
    
    // Reset caches when clearing
    this.loadedModuliForStruttura = null;
    this.loadedLavoriForModuli.clear();
    
    // Clear moduli and lavori selection and disable controls in all form groups
    this.formGroups.forEach(group => {
      group.get('moduli')?.setValue(null);
      group.get('moduli')?.disable();
      group.get('lavori')?.setValue(null);
      group.get('lavori')?.disable();
    });
  }

  private createEntryGroup(data?: ModuloLavoroEntry): FormGroup {
    if (this.readonly) {
      // In readonly mode, create disabled controls
      return this.fb.group({
        moduli: [{ value: data?.moduli || null, disabled: true }],
        lavori: [{ value: data?.lavori || null, disabled: true }],
        scadenza: [{ value: data?.scadenza || null, disabled: true }],
      });
    } else {
      // In normal mode, if we have data, enable the controls; otherwise follow disabled logic
      const shouldDisableModuli = data?.moduli ? false : this.moduliDisabled;
      // For lavori: if we have pre-existing data, enable it; otherwise always start disabled
      // until a modulo is selected (the moduli listener will enable it)
      const shouldDisableLavori = data?.lavori ? false : true;

      // Keep both moduli and lavori IDs as strings (UUIDs)
      const normalizedModuli = data?.moduli ? { ...data.moduli } : null; // Keep moduli ID as string
      const normalizedLavori = data?.lavori ? { ...data.lavori } : null; // Keep lavori ID as string too

      return this.fb.group({
        moduli: [
          { value: normalizedModuli, disabled: shouldDisableModuli },
          [Validators.required]
        ],
        lavori: [
          { value: normalizedLavori, disabled: shouldDisableLavori },
          [Validators.required]
        ],
        scadenza: [data?.scadenza || null],
      });
    }
  }

  addEntry(): void {
    const newGroup = this.createEntryGroup();
    // If moduli are currently disabled, disable the new entry's moduli control too
    if (this.moduliDisabled) {
      newGroup.get('moduli')?.disable();
    }
    // For lavori, start disabled but allow enabling when modulo is selected
    // Don't check this.lavoriDisabled global flag - let the moduli listener handle enabling
    newGroup.get('lavori')?.disable();
    
    this.formArray.push(newGroup);

    // Setup listener for the new group
    this.setupModuliListener(newGroup);
  }

  removeEntry(index: number): void {
    if (this.formArray.length > 1) {
      this.formArray.removeAt(index);
      // Clean up lavori options for the removed group
      this.lavoriByGroup.delete(index);
      this.lavoriCache.delete(index);
      // Reindex the remaining groups since indices have shifted
      this.reindexLavoriByGroup(index);
    }
  }

  private reindexLavoriByGroup(removedIndex: number): void {
    // Create new maps with updated indices
    const updatedLavoriMap = new Map<number, { id: number; name: string }[]>();
    const updatedCacheMap = new Map<
      number,
      { options: { id: number; name: string }[]; hash: string }
    >();

    this.lavoriByGroup.forEach((value, key) => {
      if (key > removedIndex) {
        // Shift index down by 1 for indices greater than removed index
        updatedLavoriMap.set(key - 1, value);
      } else if (key < removedIndex) {
        // Keep the same index for indices less than removed index
        updatedLavoriMap.set(key, value);
      }
      // Skip the removed index
    });

    this.lavoriCache.forEach((value, key) => {
      if (key > removedIndex) {
        // Shift index down by 1 for indices greater than removed index
        updatedCacheMap.set(key - 1, value);
      } else if (key < removedIndex) {
        // Keep the same index for indices less than removed index
        updatedCacheMap.set(key, value);
      }
      // Skip the removed index
    });

    this.lavoriByGroup = updatedLavoriMap;
    this.lavoriCache = updatedCacheMap;
  }

  private setupModuliListener(group: FormGroup): void {
    // Don't set up listeners in readonly mode
    if (this.readonly) {
      return;
    }

    const moduliControl = group.get('moduli');
    const lavoriControl = group.get('lavori');

    if (moduliControl && lavoriControl) {
      moduliControl.valueChanges.subscribe(
        (selectedModulo: ModuloLavoroOption | null) => {
          // Find the group index
          const groupIndex = this.formGroups.indexOf(group);

          if (selectedModulo && selectedModulo.id) {
            // Get the ID of the selected modulo
            const moduloId = selectedModulo.id;
            this.loadLavoriForModuli([moduloId], groupIndex);
          } else {
            // Clear and disable lavori when no modulo selected
            lavoriControl.setValue(null);
            lavoriControl.disable();
            // Clear lavori options for this specific group
            this.lavoriByGroup.delete(groupIndex);
            this.lavoriCache.delete(groupIndex);
          }
        }
      );
    }
  }

  private loadLavoriForModuli(
    moduliIds: (string | number)[],
    groupIndex: number,
    preserveSelection = false
  ): void {
    const moduloId = String(moduliIds[0]); // Take first ID for caching
    
    const targetGroup = this.formGroups[groupIndex];
    if (!targetGroup) {
      return;
    }
    
    // Check if we already have cached lavori data for this modulo
    if (this.loadedLavoriForModuli.has(moduloId)) {
      // Use cached data - find existing lavori options from another group
      let cachedLavoriOptions: { id: number; name: string }[] = [];
      
      // Look for cached lavori options in other groups
      for (const [_, lavoriOptions] of this.lavoriByGroup) {
        if (lavoriOptions.length > 0) {
          // Verify these are for the same modulo by checking if any group has this modulo
          cachedLavoriOptions = lavoriOptions;
          break;
        }
      }
      
      if (cachedLavoriOptions.length > 0) {
        // Use cached lavori options for this group
        this.lavoriByGroup.set(groupIndex, cachedLavoriOptions);
        this.lavoriCache.delete(groupIndex);
        
        // Enable lavori control for this specific group
        targetGroup.get('lavori')?.enable();
      }
      return;
    }
    
    this.loadedLavoriForModuli.add(moduloId);
    this.isLoadingLavori = true;

    // Save current selection if we need to preserve it
    const currentSelection = preserveSelection
      ? targetGroup.get('lavori')?.value
      : null;

    // Disable lavori control while loading, but don't clear if preserving
    targetGroup.get('lavori')?.disable();
    if (!preserveSelection) {
      targetGroup.get('lavori')?.setValue(null);
    }

    this.lavoriStore.fetchLavoriByModuli(moduliIds).subscribe({
      next: lavori => {
        const lavoriOptions = lavori.map(l => ({ id: l.id, name: l.nome }));
        
        // Store lavori options for this specific group
        this.lavoriByGroup.set(groupIndex, lavoriOptions);
        // Clear cache to force refresh
        this.lavoriCache.delete(groupIndex);
        this.isLoadingLavori = false;

        // Enable lavori control for this specific group
        targetGroup.get('lavori')?.enable();

        // Restore selection if preserving and the option is available
        if (preserveSelection && currentSelection) {
          const foundOption = lavoriOptions.find(
            opt => Number(opt.id) === Number(currentSelection.id)
          );
          
          if (foundOption) {
            targetGroup.get('lavori')?.setValue(foundOption);
          } else {
            // If exact match not found, try to find by name as fallback
            const foundByName = lavoriOptions.find(
              opt => opt.name === currentSelection.name
            );
            if (foundByName) {
              targetGroup.get('lavori')?.setValue(foundByName);
            }
          }
        }
      },
      error: () => {
        // Clear lavori options for this specific group
        this.lavoriByGroup.delete(groupIndex);
        this.lavoriCache.delete(groupIndex);
        this.loadedLavoriForModuli.delete(moduloId); // Reset on error to allow retry
        this.isLoadingLavori = false;

        // Keep lavori control disabled on error
        targetGroup.get('lavori')?.disable();
      },
    });
  }

  clearModuliRow(index: number): void {
    this.formGroups[index]?.get('moduli')?.setValue(null);
  }

  clearLavoriRow(index: number): void {
    this.formGroups[index]?.get('lavori')?.setValue(null);
  }

  shouldDisableAddButton(groupIndex: number): boolean {
    // Disable the + button if both moduli and lavori have only one option
    const lavoriForGroup = this.getLavoriForGroup(groupIndex);
    return this.moduli().length === 1 && lavoriForGroup.length === 1;
  }

  private loadLavoriForSingleModulo(
    moduloId: string,
    normalizedValues: ModuloLavoroEntry[]
  ): void {
    // Prevent duplicate calls for the same modulo
    if (this.loadedLavoriForModuli.has(moduloId)) {
      return;
    }
    
    this.loadedLavoriForModuli.add(moduloId);
    this.isLoadingLavori = true;

    this.lavoriStore.fetchLavoriByModuli([moduloId]).subscribe({
      next: lavori => {
        const lavoriOptions = lavori.map(l => ({ id: l.id, name: l.nome })); // Keep ID as string
        
        // Update global lavori array with new options
        const existingLavoriIds = this.lavori.map(l => l.id);
        const newLavori = lavoriOptions.filter(l => !existingLavoriIds.includes(l.id));
        this.lavori = [...this.lavori, ...newLavori];
        
        // Find all form groups that use this modulo and set their lavori options
        normalizedValues.forEach((normalizedValue, index) => {
          if (normalizedValue.moduli?.id === moduloId) {
            const targetGroup = this.formGroups[index];
            if (targetGroup) {
              // Store lavori options for this specific group
              this.lavoriByGroup.set(index, lavoriOptions);
              // Clear cache to force refresh
              this.lavoriCache.delete(index);
              
              // Enable lavori control for this specific group
              targetGroup.get('lavori')?.enable();
              
              // Pre-fill with the saved lavoro selection
              const savedLavoro = normalizedValue.lavori;
              if (savedLavoro && savedLavoro.id && savedLavoro.name) {
                const foundOption = lavoriOptions.find(
                  opt => opt.id === savedLavoro.id // Compare string IDs directly
                );
                
                if (foundOption) {
                  // Set the form control value to the found option
                  targetGroup.get('lavori')?.setValue(foundOption);
                } else {
                  // If exact match not found, try to find by name as fallback
                  const foundByName = lavoriOptions.find(
                    opt => opt.name === savedLavoro.name
                  );
                  if (foundByName) {
                    targetGroup.get('lavori')?.setValue(foundByName);
                  } else {
                    // If no match found in API, use the saved data directly (fallback)
                    targetGroup.get('lavori')?.setValue({
                      id: savedLavoro.id, // Keep as string
                      name: savedLavoro.name
                    });
                  }
                }
              }
            }
          }
        });
        
        this.isLoadingLavori = false;
      },
      error: () => {
        // Clear lavori options for all groups using this modulo
        normalizedValues.forEach((normalizedValue, index) => {
          if (normalizedValue.moduli?.id === moduloId) {
            this.lavoriByGroup.delete(index);
            this.lavoriCache.delete(index);
            const targetGroup = this.formGroups[index];
            if (targetGroup) {
              targetGroup.get('lavori')?.disable();
            }
          }
        });
        this.loadedLavoriForModuli.delete(moduloId); // Reset on error to allow retry
        this.isLoadingLavori = false;
      },
    });
  }
}
